import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import type { Activity, ItineraryDay } from '../../types';
import type { StopGroup } from '../../utils/photoSelection';
import { useDateLocale } from '../../utils/dateLocale';
import { CircleDashed, Icon, MapPin, Search } from '../ui/Icon';

const rowClass = 'w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors cursor-pointer';

function useDayDate() {
  const locale = useDateLocale();
  return (day: ItineraryDay) => format(parseISO(day.date), 'EEE d MMM', { locale });
}

export function DayPickerList({ days, onPick }: { days: ItineraryDay[]; onPick: (dayId: string | null) => void }) {
  const { t } = useTranslation();
  const dayDate = useDayDate();

  return (
    <div className="max-h-80 overflow-y-auto py-1">
      <button type="button" role="menuitem" onClick={() => onPick(null)} className={`${rowClass} text-slate-500`}>
        <Icon as={CircleDashed} size={15} className="text-slate-400" />
        {t('gallery.unassignedDay')}
      </button>
      <div className="my-1 border-t border-slate-100 dark:border-slate-800" />
      {days.map(d => (
        <button key={d.id} type="button" role="menuitem" onClick={() => onPick(d.id)} className={rowClass}>
          <span className="w-6 h-6 shrink-0 rounded-full bg-teal-50 dark:bg-teal-950/50 text-teal-700 dark:text-teal-300 text-xs font-semibold flex items-center justify-center tabular-nums">
            {d.dayNumber}
          </span>
          <span className="font-medium">{t('gallery.dayOption', { number: d.dayNumber })}</span>
          <span className="ml-auto text-xs text-slate-400">{dayDate(d)}</span>
        </button>
      ))}
    </div>
  );
}

/** ADR-0056: every stop of the trip, by day; photos from another day move to it. */
export function StopPickerList({ groups, onPick }: { groups: StopGroup[]; onPick: (stop: Activity | null) => void }) {
  const { t } = useTranslation();
  const dayDate = useDayDate();
  const [query, setQuery] = useState('');

  const visible = useMemo(() => {
    const q = query.trim().toLocaleLowerCase();
    if (!q) return groups;
    return groups
      .map(g => ({ ...g, stops: g.stops.filter(s => s.title.toLocaleLowerCase().includes(q)) }))
      .filter(g => g.stops.length > 0);
  }, [groups, query]);

  return (
    <div>
      <div className="p-2 border-b border-slate-100 dark:border-slate-800">
        <label className="flex items-center gap-2 px-2.5 h-9 rounded-lg bg-slate-50 dark:bg-slate-800 focus-within:ring-2 focus-within:ring-teal-500/40">
          <Icon as={Search} size={14} className="text-slate-400 shrink-0" />
          <input
            autoFocus
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={t('gallery.selection.searchStop')}
            aria-label={t('gallery.selection.searchStop')}
            className="flex-1 min-w-0 bg-transparent text-sm text-slate-700 dark:text-slate-200 placeholder:text-slate-400 outline-none"
          />
        </label>
        <p className="mt-1.5 px-1 text-[11px] leading-snug text-slate-400">{t('gallery.selection.stopHint')}</p>
      </div>

      <div className="max-h-80 overflow-y-auto pb-1">
        {!query && (
          <button type="button" role="menuitem" onClick={() => onPick(null)} className={`${rowClass} text-slate-500`}>
            <Icon as={CircleDashed} size={15} className="text-slate-400" />
            {t('gallery.noStop')}
          </button>
        )}
        {visible.map(({ day, stops }) => (
          <div key={day.id} role="group" aria-label={t('gallery.dayOption', { number: day.dayNumber })}>
            <div className="sticky top-0 z-10 flex items-baseline gap-2 px-3 pt-2.5 pb-1 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm">
              <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-teal-600 dark:text-teal-400">
                {t('gallery.dayOption', { number: day.dayNumber })}
              </span>
              <span className="text-[11px] text-slate-400">{dayDate(day)}</span>
            </div>
            {stops.map(s => (
              <button key={s.id} type="button" role="menuitem" onClick={() => onPick(s)} title={s.title} className={rowClass}>
                <Icon as={MapPin} size={14} className="text-slate-300 dark:text-slate-600 shrink-0" />
                <span className="truncate">{s.title}</span>
              </button>
            ))}
          </div>
        ))}
        {visible.length === 0 && (
          <p className="px-3 py-6 text-center text-sm text-slate-400">{t('gallery.selection.noStopMatch')}</p>
        )}
      </div>
    </div>
  );
}
